import { useFormikContext } from 'formik'
import * as React from 'react'
import { useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import { useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useTranslatable } from 'src/hooks/locale/utils'
import { useActiveSemester } from 'src/hooks/semesters'
import Checkbox from 'src/views/components/forms/formik/Checkbox'
import { Form } from 'src/views/components/forms/formik/Form'
import { SearchCancelButtons } from 'src/views/components/forms/formik/SearchCancelButtons'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import { GRID_THREE } from '../programs/AdministratorProgramPage/constants'
import {
  DEFAULT_PER_PAGE,
  formValuesToSearchParams,
  parseFormValues,
  type StatementsFormValues,
} from './statementsQuery'

export default function StatementsFilter(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams()
  const activeSemester = useActiveSemester()

  const initialValues = React.useMemo((): StatementsFormValues => {
    const values = parseFormValues(searchParams)
    return { ...values, semId: values.semId || (activeSemester?.id ?? '') }
  }, [searchParams, activeSemester?.id])

  const handleSubmit = React.useCallback(
    (values: StatementsFormValues) => {
      setSearchParams(formValuesToSearchParams(values, searchParams.get('perPage') ?? String(DEFAULT_PER_PAGE)))
    },
    [searchParams, setSearchParams]
  )

  return (
    <Form initialValues={initialValues} onSubmit={handleSubmit} classNames={{ form: 'w-full mb-1' }} enableReinitialize>
      <FilterFields />
    </Form>
  )
}

function FilterFields(): JSX.Element {
  const t = useTranslatable()
  const formik = useFormikContext<StatementsFormValues>()
  const [searchParams, setSearchParams] = useSearchParams()
  const headers = useAuthenticatedHeaders()
  const listParams = React.useMemo(() => ({ headers }), [headers])

  const { data: templates, isLoading: templatesLoading } = useApi({
    endpoint: Api.getAdministratorListsStatementTemplates,
    params: listParams,
    suspense: false,
  })
  const { data: statuses, isLoading: statusesLoading } = useApi({
    endpoint: Api.getAdministratorListsStatementStatuses,
    params: listParams,
    suspense: false,
  })
  const { data: faculties, isLoading: facultiesLoading } = useApi({
    endpoint: Api.getAdministratorListsFaculties,
    params: listParams,
    suspense: false,
  })
  const { data: degrees, isLoading: degreesLoading } = useApi({
    endpoint: Api.getAdministratorListsDegrees,
    params: listParams,
    suspense: false,
  })
  const { data: semesters, isLoading: semestersLoading } = useApi({
    endpoint: Api.getSemesters,
    params: listParams,
    suspense: false,
  })

  const anyOption = React.useMemo(() => ({ value: '', label: t('common:all') }), [t])

  // Passive templates stay listed: old statements are still searched by them
  const templateOptions = React.useMemo(
    () => [
      anyOption,
      ...(templates ?? []).map((item) => ({
        value: String(item.id),
        label: item.status === 0 ? `${item.name} (${t('common:inactive')})` : item.name,
      })),
    ],
    [templates, anyOption, t]
  )
  const statusOptions = React.useMemo(
    () => [anyOption, ...(statuses ?? []).map((item) => ({ value: String(item.id), label: item.name }))],
    [statuses, anyOption]
  )
  const facultyOptions = React.useMemo(
    () => [anyOption, ...(faculties?.data ?? []).map((item) => ({ value: String(item.id), label: item.name }))],
    [faculties, anyOption]
  )
  const degreeOptions = React.useMemo(
    () => [anyOption, ...(degrees ?? []).map((item) => ({ value: String(item.id), label: item.name }))],
    [degrees, anyOption]
  )
  const semesterOptions = React.useMemo(
    () =>
      (semesters ?? []).map((semester) => ({
        value: semester.id,
        label: semester.name + (semester.isActive ? t('common:current') : ''),
      })),
    [semesters, t]
  )

  const isFresh = [...searchParams.keys()].every((key) => key === 'page' || key === 'perPage')

  const handleCancel = React.useCallback(() => {
    setSearchParams({})
  }, [setSearchParams])

  return (
    <fieldset disabled={formik.isSubmitting} className="flex w-full flex-col gap-4">
      <div className="flex flex-col gap-2">
        <TextInput
          type="text"
          name="search"
          label={t('common:search_for_keywords')}
          placeholder={t('common:enter_your_keyword')}
        />
        <Checkbox name="searchInBody" label={t('statement:search_in_text')} />
      </div>

      <div className={GRID_THREE}>
        <SelectInput
          label={t('statement:template')}
          name="templateId"
          options={templateOptions}
          isLoading={templatesLoading}
        />
        <SelectInput label={t('common:status')} name="status" options={statusOptions} isLoading={statusesLoading} />
        <SelectInput label={t('faculty:faculty')} name="facId" options={facultyOptions} isLoading={facultiesLoading} />
        <SelectInput label={t('person:degree')} name="degree" options={degreeOptions} isLoading={degreesLoading} />
        <SelectInput label={t('common:semester')} name="semId" options={semesterOptions} isLoading={semestersLoading} />
      </div>

      <SearchCancelButtons isDirty={formik.dirty} canCancel={isFresh} onCancel={handleCancel} />
    </fieldset>
  )
}
